import { useId, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ISchedulerData,
  ISchedulerDependency,
  ISchedulerTask,
  TaskStatus,
} from '@interfaces/IScheduler';
import { STATUS_KEY } from '../../lib/statusLabels';
import { useTaskLadder } from '../../hooks/useActivityLadder';
import { freezeOf, isFrozen } from '../../lib/freeze';
import { DependenciesSection } from './DependenciesSection';
import { ChecklistAuthoring } from './ChecklistAuthoring';
import { checklistSubject } from '../../lib/checklistItems';
import { GroupSelect } from './GroupSelect';
import { ProfessionPicker } from './ProfessionPicker';
import { TaskColorPicker } from './TaskColorPicker';
import styles from './TaskPanel.module.css';
import { nameCollision } from '../../lib/nameCollision';
import { taskDatesProblem } from '../../lib/taskDates';
import { parseCrewSize } from '../../lib/crewSize';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';

interface ITaskPanelEditTabProps {
  draft: ISchedulerTask;
  setDraft: Dispatch<SetStateAction<ISchedulerTask | null>>;
  data: ISchedulerData;
  mode: 'create' | 'edit';
  openGroupSelectOnMount?: boolean;
  onAddDependency?: (dependency: ISchedulerDependency) => void;
  onUpdateDependency?: (dependency: ISchedulerDependency) => void;
  onDeleteDependency?: (dependencyId: string) => void;
  /** The loaded schedule — a committed task's checklist amendment goes to it. */
  scheduleNumber?: string;
}

export function TaskPanelEditTab({ draft, setDraft, data, mode, openGroupSelectOnMount, onAddDependency, onUpdateDependency, onDeleteDependency, scheduleNumber }: ITaskPanelEditTabProps) {
  const { t } = useTranslation();
  const fieldId = useId();
  // Fields the field has already answered are shown, disabled, with the reason —
  // never accepted here and rejected on Save (2026-09-20 amendment, decision 2).
  const freeze = freezeOf(useTaskLadder(draft).rung, draft.kind);
  const startFrozen = isFrozen(freeze, 'startDate');

  // D-ORCA owns the schedule, so renaming and re-grouping persist like any
  // other edit. A name collision is a soft warning, never a Save blocker — but
  // a committed namesake means the COMMIT will be refused (one activity per
  // name in Trusstor's project sequence), so that one says so.
  const collision = nameCollision(draft, data.tasks);
  // The drawer's Save is gated on the same rule; this says why it is off.
  const datesProblem = taskDatesProblem(draft);

  return (
    <ScrollArea className={styles.body} viewportClassName={styles.bodyContent}>
      <div className={styles.field}>
        <label htmlFor={`${fieldId}-name`}>{t('scheduler.panel.fields.name')}</label>
        <input
          id={`${fieldId}-name`}
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          autoFocus
          aria-invalid={collision !== null || undefined}
        />
        {collision && (
          <span className={styles.fieldError} role="alert">
            {t(collision === 'committed' ? 'scheduler.save.duplicateCommittedName' : 'scheduler.save.duplicateName')}
          </span>
        )}
      </div>

      <div className={styles.row}>
        <div className={styles.field}>
          <label htmlFor={`${fieldId}-group`}>{t('scheduler.panel.fields.group')}</label>
          {draft.parentId != null ? (
            // A sub-task inherits its parent's group — there's no reparent UI, so
            // the group is fixed. Show it as a read-only chip instead of a select.
            <span id={`${fieldId}-group`} className={styles.groupReadonlyChip}>
              {data.groups.find((group) => group.id === draft.groupId)?.name ?? draft.groupId}
            </span>
          ) : (
            <GroupSelect
              id={`${fieldId}-group`}
              siteId={data.siteId}
              groups={data.groups}
              value={draft.groupId}
              onChange={(groupId) => setDraft({ ...draft, groupId })}
              openOnMount={mode === 'create' && openGroupSelectOnMount}
            />
          )}
        </div>

        {/* Status is only DRAFTED for a task being created. On an existing task
            it's an immediate-write surface — the header status pill is the
            picker (no Save) — so a drafted copy here could only go stale. */}
        {mode === 'create' && (
          <div className={styles.field}>
            <label htmlFor={`${fieldId}-status`}>{t('scheduler.panel.fields.status')}</label>
            <select
              id={`${fieldId}-status`}
              value={draft.status}
              onChange={(e) => setDraft({ ...draft, status: e.target.value as TaskStatus })}
            >
              {/* D-ORCA owns the schedule, so every status — including 'blocked' —
                  is a stored, legal value. */}
              {(Object.keys(STATUS_KEY) as TaskStatus[]).map((status) => (
                <option key={status} value={status}>{t(`scheduler.status.${STATUS_KEY[status]}`)}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className={styles.row}>
        <div className={styles.field}>
          <label htmlFor={`${fieldId}-start`}>{t('scheduler.panel.fields.start')}</label>
          <input
            id={`${fieldId}-start`}
            type="date"
            value={draft.start}
            disabled={startFrozen}
            aria-invalid={datesProblem !== null || undefined}
            onChange={(e) => setDraft({ ...draft, start: e.target.value })}
          />
        </div>
        <div className={styles.field}>
          <label htmlFor={`${fieldId}-end`}>{t('scheduler.panel.fields.end')}</label>
          <input
            id={`${fieldId}-end`}
            type="date"
            value={draft.end}
            disabled={isFrozen(freeze, 'endDate')}
            aria-invalid={datesProblem !== null || undefined}
            onChange={(e) => setDraft({ ...draft, end: e.target.value })}
          />
        </div>
      </div>
      {datesProblem && (
        <span className={styles.fieldError} role="alert" data-testid="edit-dates-error">
          {t(`scheduler.panel.dates.${datesProblem}`)}
        </span>
      )}
      {(freeze !== 'none' || draft.committed) && (
        <p className={styles.freezeHint} data-testid="edit-freeze-hint">
          {t(freeze !== 'none' ? `scheduler.freeze.${freeze}` : 'scheduler.freeze.committed')}
        </p>
      )}

      <label className={styles.toggle} htmlFor={`${fieldId}-isMilestone`}>
        <input
          id={`${fieldId}-isMilestone`}
          type="checkbox"
          checked={draft.isMilestone}
          onChange={(e) => setDraft({ ...draft, isMilestone: e.target.checked, end: e.target.checked ? draft.start : draft.end })}
        />
        <span>{t('scheduler.panel.fields.milestone')}</span>
      </label>

      <div className={styles.row}>
        {/* Progress mirrors status: drafted only at create; on an existing task
            the Overview slider / area board writes it instantly. */}
        {mode === 'create' && (
          <div className={styles.field}>
            <label htmlFor={`${fieldId}-progress`}>{t('scheduler.panel.fields.progress')}</label>
            <input
              id={`${fieldId}-progress`}
              type="number"
              min={0}
              max={100}
              value={draft.progressPercent}
              onChange={(e) => setDraft({ ...draft, progressPercent: Number(e.target.value) })}
            />
          </div>
        )}
        <div className={styles.field}>
          <label htmlFor={`${fieldId}-workerCount`}>{t('scheduler.panel.fields.workers')}</label>
          <input
            id={`${fieldId}-workerCount`}
            type="number"
            min={0}
            step={1}
            value={draft.workerCount ?? ''}
            onChange={(e) => {
              // A crew is a whole number ≥ 0 (the server's rule too); anything
              // else is refused here rather than saved and rejected.
              const workerCount = parseCrewSize(e.target.value);
              if (workerCount !== 'invalid') setDraft({ ...draft, workerCount });
            }}
          />
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor={`${fieldId}-profession`}>{t('scheduler.panel.fields.profession')}</label>
        <ProfessionPicker
          id={`${fieldId}-profession`}
          siteId={data.siteId}
          value={draft.profession}
          // Fixed at the commit (2026-09-23 amendment), like the areas.
          disabled={draft.committed || isFrozen(freeze, 'profession')}
          onChange={(profession) => setDraft({ ...draft, profession })}
        />
      </div>

      <div className={styles.field}>
        <label>{t('scheduler.panel.fields.color')}</label>
        <TaskColorPicker
          value={draft.color}
          onChange={(color) => setDraft({ ...draft, color })}
        />
      </div>

      <div className={styles.field}>
        <label htmlFor={`${fieldId}-notes`}>{t('scheduler.panel.fields.notes')}</label>
        <textarea
          id={`${fieldId}-notes`}
          value={draft.notes ?? ''}
          onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
          rows={3}
        />
      </div>

      <DependenciesSection
        task={draft}
        mode={mode}
        data={data}
        onAdd={onAddDependency}
        onUpdate={onUpdateDependency}
        onDelete={onDeleteDependency}
      />

      <ChecklistAuthoring
        task={checklistSubject(draft, data.tasks)}
        siteId={data.siteId}
        scheduleNumber={scheduleNumber}
        onChange={(patch) => setDraft({ ...draft, ...patch })}
      />
    </ScrollArea>
  );
}
